import { UnitGpsDto } from '@mark43/cad-api';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { reaction } from 'mobx';
import { isEqual } from 'lodash';
import { createStreamListener, useStream } from '../socket/use-stream';
import { useUnitAssignment } from '../unit-assignment/unit-assignment-context';
import { UnitLocationStore } from './unit-location-store';
import { UnitLocationStoreContext } from './unit-location-store-context';
import { useStreamUnitLocation } from '../common/hooks/use-stream-unit-location';
import { isValidLatLng } from '../location/helpers';
import { GeoPoint } from '../location/types';
import { UnitLocationContext } from './unit-location-context';
import { usePrevious } from '../common/hooks/use-previous';

export function UnitLocationProvider({ children }: { children: ReactNode }) {
    const { unitId } = useUnitAssignment() ?? {};
    const unitLocationStore = useState(() => new UnitLocationStore())[0];
    const [unitLocation, setUnitLocation] = useState<undefined | GeoPoint>(undefined);
    const unitLocationData = useStreamUnitLocation({ unitId });
    const previousUnitLocationData = usePrevious(unitLocationData);

    useEffect(() => {
        return reaction(
            () => unitLocationStore.unitLocation,
            (value, prev) => {
                if (!isEqual(prev, value)) {
                    setUnitLocation(value);
                }
            }
        );
    }, [unitLocationStore]);

    const unitGeoLocation = useMemo(() => {
        const { latitude, longitude } = unitLocationData?.consolidatedLocationView || {};

        if (!isValidLatLng(latitude, longitude)) {
            return undefined;
        }

        return { latitude, longitude } as GeoPoint;
    }, [unitLocationData]);

    useEffect(() => {
        if (unitGeoLocation) {
            setUnitLocation(unitGeoLocation);
        }
    }, [unitGeoLocation]);

    const prevUnitGeoLocation = useMemo(() => {
        const { latitude, longitude } = previousUnitLocationData?.consolidatedLocationView || {};

        if (!isValidLatLng(latitude, longitude)) {
            return undefined;
        }

        return { latitude, longitude } as GeoPoint;
    }, [previousUnitLocationData]);

    if (!isEqual(prevUnitGeoLocation, unitGeoLocation)) {
        unitLocationStore.setUnitLocation(unitGeoLocation);
    }

    const listener = useMemo(() => {
        if (!unitId) {
            unitLocationStore.setUnitGpsLocation({} as UnitGpsDto);
            return;
        }

        return createStreamListener({
            streamNames: ['gps'],
            onMessage: (message) => {
                if (message.payload?.unitId === unitId) {
                    unitLocationStore.setUnitGpsLocation(message.payload);
                    unitLocationStore.setUnitLocation({
                        latitude: message.payload.lat,
                        longitude: message.payload.lng,
                    });
                }
            },
        });
    }, [unitId, unitLocationStore]);

    useStream(listener);

    return (
        <UnitLocationStoreContext.Provider value={unitLocationStore}>
            <UnitLocationContext.Provider value={unitLocation}>
                {children}
            </UnitLocationContext.Provider>
        </UnitLocationStoreContext.Provider>
    );
}
